














































































































































































































































































































































































































































































import { Component, Vue, Watch } from 'vue-property-decorator';
import { BACKEND_URL } from '@/helpers/constants';
import Chapter from '@/models/Chapter';
import Step from '@/models/Step';
import TrialVideoProgress from '@/models/TrialVideoProgress';
import { getTrialSteps, getTrialProgress, postTrialProgress } from '@/api/api';
import AuthModule from '../../store/AuthModule';

interface Segment { start: number; end: number; duration: number; title: string; flex: number; }

@Component
export default class Trial extends Vue {
  loading: boolean = true;
  currentStep: number = 0;
  currentChapterIdx: number = -1;
  
  isPlaying: boolean = false;
  userInactive: boolean = false;
  inactivityTimer: any = null;
  currentTime: number = 0;
  totalDuration: number = 0;
  timelineHoverIdx: number = -1;  

  chapterWatchTime: number[] = [];
  lastTrackedTime: number = -1;
  steps: Step[] = [];
  allChapterWatchTime: number[][] = [];
  allChapterCompleted: boolean[][] = [];
  expandedSteps: boolean[] = [];
  
  showTrialPopup: boolean = false;
  showLockedPopup: boolean = false;

  lockedPopupType: 'locked' | 'unavailable' = 'locked';

  async created() { await this.loadSteps(); }

  // --- DATA LOADING & PARSING ---

  async loadSteps() {
      this.loading = true;
      try {
        const res = await getTrialSteps();
        const groupedSteps = res.data;
        
        this.steps = groupedSteps.map((group: any) => {
          const chapters: Chapter[] = group.chapters.map((ch: any) => ({
            id: ch.id,
            start: 0, 
            end: 0,
            duration: 0,
            title: ch.title,
            videoUrl: ch.videoUrl || '',
            isMandatory: ch.isMandatory || false,
            isLocked: ch.isLocked || false,
            isAvailable: ch.isAvailable !== undefined ? ch.isAvailable : true,
            documentUrl: ch.documentUrl || '',
            chapterLevel: ch.chapterLevel || '',
            displayLabel: ''
          }));

          return {
            title: group.stepTitle || '',
            description: (group.chapters[0] && group.chapters[0].description) || '',
            completed: false,
            progress: 0,
            duration: 0,
            chapters
          };
        });

        this.generateHierarchicalLabels();
        this.allChapterWatchTime = this.steps.map(s => s.chapters.map(() => 0));
        this.allChapterCompleted = this.steps.map(s => s.chapters.map(() => false));
        this.expandedSteps = this.steps.map(() => true);
        
        await this.loadProgressFromBackend();
        
        this.initLocalWatchTime();

        if (this.steps.length > 0 && this.steps[0].chapters.length > 0) {
          this.selectChapter(0, 0);
        }

      } catch (e) {
        console.error('Trial load error', e);
      } finally {
        this.loading = false;
      }
    }



    onVideoPause() {
      this.isPlaying = false;
        const step = this.steps[this.currentStep];
        const ch = step.chapters[this.currentChapterIdx];
        if (!ch || !ch.id) return;
        
        const watchedSeconds = this.chapterWatchTime[this.currentChapterIdx] || 0;
        const lastPosition = this.currentTime;
        const isCompleted = this.isChapterValidated(this.currentStep, this.currentChapterIdx);
        
        this.saveProgressToBackend(ch.id, watchedSeconds, lastPosition, isCompleted);
      }



  // Generate labels like A, B, A-1, A-2 on frontend
  generateHierarchicalLabels() {
    this.steps.forEach(step => {
      step.chapters.sort((a: Chapter, b: Chapter) => {
        const la = a.chapterLevel || '';
        const lb = b.chapterLevel || '';
        if (!la && !lb) return 0;
        if (!la) return -1;
        if (!lb) return 1;
        const partsA = la.split('-');
        const partsB = lb.split('-');
        if (partsA[0] !== partsB[0]) return partsA[0].localeCompare(partsB[0]);
        if (partsA.length === 1 && partsB.length === 1) return 0;
        if (partsA.length === 1) return -1;
        if (partsB.length === 1) return 1;
        return parseInt(partsA[1]) - parseInt(partsB[1]);
      });

      let topCounter = 0;
      step.chapters.forEach((ch, i) => {
        if (this.isTopLevel(ch)) {
          ch.displayLabel = this.getLetterFromIndex(topCounter++);
          let subCounter = 1;
          for (let j = i + 1; j < step.chapters.length; j++) {
            const potentialChild = step.chapters[j];
            if (this.isChildOf(potentialChild, ch)) {
              potentialChild.displayLabel = ch.displayLabel + '-' + subCounter++;
            }
          }
        }
      });
    });
  }

  // --- HIERARCHY HELPERS ---
  isTopLevel(ch: Chapter): boolean {
    const lvl = ch.chapterLevel || '';
    return lvl === '' || lvl.indexOf('-') === -1;
  }

  isChildOf(ch: Chapter, parent: Chapter): boolean {
    const lvl = ch.chapterLevel || '';
    const pLvl = parent.chapterLevel || '';
    if (!lvl || !pLvl || lvl.indexOf('-') === -1) return false;
    return lvl.startsWith(pLvl + '-'); 
  }
  
  hasChildren(step: Step, parent: Chapter): boolean {
    return step.chapters.some(ch => this.isChildOf(ch, parent));
  }

  getLetterFromIndex(index: number): string {
    let letter = '';
    let i = index;
    while (i >= 0) {
      letter = String.fromCharCode(i % 26 + 65) + letter;
      i = Math.floor(i / 26) - 1;
    }
    return letter;
  }

  // --- VALIDATION LOGIC ---
  getRequiredDuration(stepIdx: number, chIdx: number): number {
    const step = this.steps[stepIdx];
    if (!step || !step.chapters[chIdx]) return 15;
    const ch = step.chapters[chIdx];
    return ch.duration > 0 ? ch.duration * 0.5 : 15;
  }


  isChapterValidated(stepIdx: number, chIdx: number): boolean {
    if (this.allChapterCompleted[stepIdx] && this.allChapterCompleted[stepIdx][chIdx]) {
      return true;
    }
    const watched = (this.allChapterWatchTime[stepIdx] && this.allChapterWatchTime[stepIdx][chIdx]) || 0;
    const required = this.getRequiredDuration(stepIdx, chIdx);
    if (required <= 0) return false;
    return watched >= (required - 1.5);
  }

  isFamilyValidated(stepIdx: number, parentIdx: number): boolean {
    if (!this.isChapterValidated(stepIdx, parentIdx)) return false;
    const step = this.steps[stepIdx];
    const parent = step.chapters[parentIdx];
    for (let i = 0; i < step.chapters.length; i++) {
      if (this.isChildOf(step.chapters[i], parent)) {
        if (!this.isChapterValidated(stepIdx, i)) return false;
      }
    }
    return true;
  }

  isModuleCompleted(sIdx: number): boolean {
    const step = this.steps[sIdx];
    if (!step || step.chapters.length === 0) return false;
    for (let i = 0; i < step.chapters.length; i++) {
      if (!this.isChapterValidated(sIdx, i)) return false;
    }
    return true;
  }

  get isCurrentValidated(): boolean {
    return this.isChapterValidated(this.currentStep, this.currentChapterIdx);
  }

  get currentProgressPercent(): number {
    const watched = (this.allChapterWatchTime[this.currentStep] && this.allChapterWatchTime[this.currentStep][this.currentChapterIdx]) || 0;
    const required = this.getRequiredDuration(this.currentStep, this.currentChapterIdx);
    if (required <= 0 || required >= 99999) return 0;
    return Math.min(100, (watched / required) * 100);
  }

  get globalProgressPercent(): number {
    let totalMandatory = 0;
    let completedMandatory = 0;
    this.steps.forEach((s, sIdx) => {
      s.chapters.forEach((ch, cIdx) => {
        if (ch.isMandatory) {
          totalMandatory++;
          if (this.isChapterValidated(sIdx, cIdx)) completedMandatory++;
        }
      });
    });
    if (totalMandatory === 0) return 0;
    return Math.round((completedMandatory / totalMandatory) * 100);
  }

  // --- NAVIGATION ---
  selectStep(idx: number) {
    this.saveCurrentProgress();
    this.currentStep = idx;
    this.currentChapterIdx = -1;
    this.isPlaying = false;
    this.currentTime = 0;
    this.$set(this.expandedSteps, idx, true);
    if (this.steps[idx].chapters.length > 0) {
      this.currentChapterIdx = 0;
    }
    this.initLocalWatchTime();
  }

  selectChapter(sIdx: number, cIdx: number) {
    this.saveCurrentProgress();
    this.currentStep = sIdx;
    this.currentChapterIdx = cIdx;
    this.isPlaying = false;
    this.currentTime = 0;
    this.$set(this.expandedSteps, sIdx, true);
    this.initLocalWatchTime();
    
    this.$nextTick(() => {
      const v = this.$refs.videoPlayer as HTMLVideoElement;
      if (v && this.allChapterWatchTime[sIdx] && this.allChapterWatchTime[sIdx][cIdx]) {
        const savedTime = this.allChapterWatchTime[sIdx][cIdx];
        const ch = this.steps[sIdx].chapters[cIdx];
        if (ch.duration > 0) {
          v.currentTime = Math.min(savedTime, ch.duration - 0.5);
        }
      }
    });
  }

  initLocalWatchTime() {
    if (!this.steps[this.currentStep]) return;
    const saved = this.allChapterWatchTime[this.currentStep] || [];
    this.chapterWatchTime = [...saved];
    this.lastTrackedTime = -1;
  }

  saveCurrentProgress() {
      if (this.steps[this.currentStep]) {
        this.$set(this.allChapterWatchTime, this.currentStep, [...this.chapterWatchTime]);
        const isCompleted = this.isChapterValidated(this.currentStep, this.currentChapterIdx);
        if (isCompleted) {
          if (!this.allChapterCompleted[this.currentStep]) {
            this.$set(this.allChapterCompleted, this.currentStep, []);
          }
          this.$set(this.allChapterCompleted[this.currentStep], this.currentChapterIdx, true);
        }

        this.checkStepCompletion(this.currentStep);
        
        const ch = this.steps[this.currentStep].chapters[this.currentChapterIdx];
        if (ch && ch.id) {
          const watchedSeconds = this.chapterWatchTime[this.currentChapterIdx] || 0;
          this.saveProgressToBackend(ch.id, watchedSeconds, this.currentTime, isCompleted);
        }
      }
    }

  checkStepCompletion(sIdx: number) {
    const step = this.steps[sIdx];
    step.completed = this.isModuleCompleted(sIdx);
  }

  goNext() {
    const step = this.steps[this.currentStep];
    if (this.currentChapterIdx < step.chapters.length - 1) {
      this.selectChapter(this.currentStep, this.currentChapterIdx + 1);
    } else if (this.currentStep < this.steps.length - 1) {
      this.selectStep(this.currentStep + 1);
    }
  }

  goPrev() {
    if (this.currentChapterIdx > 0) {
      this.selectChapter(this.currentStep, this.currentChapterIdx - 1);
    } else if (this.currentStep > 0) {
      this.selectStep(this.currentStep - 1);
    }
  }

  // --- VIDEO LOGIC ---
  get currentVideoUrl(): string {
    const step = this.steps[this.currentStep];
    if (!step) return '';
    const ch = step.chapters[this.currentChapterIdx];
    if (!ch) return '';
    const url = ch.videoUrl;
    if (!url) return '';
    return (url.startsWith('http') || url.startsWith('/')) ? (url.startsWith('/') ? BACKEND_URL + url : url) : BACKEND_URL + url;
  }



    onMetadataLoaded(e: Event) {
      const v = e.target as HTMLVideoElement;
      if (!v) return;
      this.totalDuration = v.duration;
      
      const step = this.steps[this.currentStep];
      const currentCh = step.chapters[this.currentChapterIdx];
      if (currentCh) {
        currentCh.duration = v.duration;
        currentCh.start = 0;
        currentCh.end = v.duration;
        step.duration = v.duration;

        const savedTime = (this.allChapterWatchTime[this.currentStep] && this.allChapterWatchTime[this.currentStep][this.currentChapterIdx]) || 0;
        if (savedTime > 0) {
          v.currentTime = Math.min(savedTime, v.duration - 0.5);
        }
      }
    }


    onTimeUpdate(e: Event) {
        const v = e.target as HTMLVideoElement;
        if (!v) return;
        const now = v.currentTime;
        this.currentTime = now;

        if (this.lastTrackedTime >= 0 && !v.paused) {
          const delta = now - this.lastTrackedTime;
          if (delta > 0 && delta < 2) {
            const idx = this.currentChapterIdx;
            const currentVal = this.chapterWatchTime[idx] || 0;
            if (now > currentVal && now <= currentVal + 2) {
              this.$set(this.chapterWatchTime, idx, now);
              this.saveCurrentProgress();
            }
          }
        }
        this.lastTrackedTime = now;
      }


  onVideoEnded() {
    this.isPlaying = false;
    const idx = this.currentChapterIdx;
    const currentCh = this.steps[this.currentStep].chapters[idx];
    if (currentCh.duration > 0) {
       this.$set(this.chapterWatchTime, idx, currentCh.duration);
    }
    this.saveCurrentProgress();
  }

  // --- PLAYER CONTROLS ---
  togglePlay() {
    const v = this.$refs.videoPlayer as HTMLVideoElement;
    if (!v) return;
    if (v.paused) {
      v.play();
      const wrapper = this.$el.querySelector('.video-wrapper') as HTMLElement;
      if (wrapper && !document.fullscreenElement) {
        wrapper.requestFullscreen().catch(() => {});
      }
    } else {
      v.pause();
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
    }
    this.resetInactivityTimer();
  }

  toggleFullscreen() {
    const el = this.$el.querySelector('.video-wrapper') as HTMLElement;
    if (!el) return;
    if (!document.fullscreenElement) el.requestFullscreen(); else document.exitFullscreen();
  }

  seekToSegment(seg: Segment) {
    const v = this.$refs.videoPlayer as HTMLVideoElement;
    if (v) {
      v.currentTime = seg.start;
      v.play();
    }
  }

  getSegmentFill(seg: Segment): number {
    if (this.currentTime < seg.start) return 0;
    if (this.currentTime >= seg.end) return 100;
    return ((this.currentTime - seg.start) / (seg.end - seg.start)) * 100;
  }

  onTimelineClick(e: MouseEvent) {
    const v = this.$refs.videoPlayer as HTMLVideoElement;
    if(!v) return;
    const t = e.currentTarget as HTMLElement;
    const rect = t.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    v.currentTime = pos * this.totalDuration;
  }
  
  onTimelineHover(e: MouseEvent) { }

  resetInactivityTimer() {
    this.userInactive = false;
    clearTimeout(this.inactivityTimer);
    this.inactivityTimer = setTimeout(() => this.userInactive = true, 3000);
  }

  // --- GETTERS & UTILS ---
  get currentChapterTitle() {
    const step = this.steps[this.currentStep];
    const ch = step && step.chapters[this.currentChapterIdx];
    return (ch && ch.title) || (step && step.title) || '';
  }
  get currentChapterLabel() {
    const step = this.steps[this.currentStep];
    const ch = step && step.chapters[this.currentChapterIdx];
    return ch ? ch.displayLabel : '';
  }
  get currentChapterIsMandatory() {
    const step = this.steps[this.currentStep];
    const ch = step && step.chapters[this.currentChapterIdx];
    return ch && ch.isMandatory;
  }
  get currentChapterHasDocument() {
    const step = this.steps[this.currentStep];
    const ch = step && step.chapters[this.currentChapterIdx];
    return !!(ch && ch.documentUrl);
  }
  get currentChapterDoc() {
    const step = this.steps[this.currentStep];
    const ch = step && step.chapters[this.currentChapterIdx];
    return (ch && ch.documentUrl) || '';
  }

  get segments(): Segment[] {
    const step = this.steps[this.currentStep];
    const ch = step && step.chapters ? step.chapters[this.currentChapterIdx] : undefined;
    if (!ch || !ch.duration) return [];
    
    return [{
      start: 0,
      end: ch.duration,
      duration: ch.duration,
      title: ch.title,
      flex: 1
    }];
  }

  get currentTimeFormatted() { return this.fmtTime(this.currentTime); }
  get durationFormatted() { return this.fmtTime(this.totalDuration); }
  fmtTime(s: number) { 
    const m = Math.floor(s/60); 
    const sec = Math.floor(s%60); 
    return `${m}:${sec.toString().padStart(2,'0')}`; 
  }

  getDocumentUrl(url: string) { return url.startsWith('http') ? url : BACKEND_URL + url; }

  // --- POPUP LOGIC ---
  get remainingMandatoryChapters() {
    const list: any[] = [];
    this.steps.forEach((s, sIdx) => {
      s.chapters.forEach((c, cIdx) => {
        if (c.isMandatory && !this.isChapterValidated(sIdx, cIdx)) {
          const watched = (this.allChapterWatchTime[sIdx] && this.allChapterWatchTime[sIdx][cIdx]) || 0;
          const req = this.getRequiredDuration(sIdx, cIdx);
          const percent = (req > 0 && req < 99999) ? (watched / req) * 100 : 0;
          list.push({
            stepIdx: sIdx, chIdx: cIdx,
            chTitle: c.title, chLevel: c.displayLabel,
            percent: Math.min(100, percent)
          });
        }
      });
    });
    return list;
  }

  get allMandatoryCompleted() { return this.remainingMandatoryChapters.length === 0; }

  get userHasSubscription(): boolean {
    return AuthModule.isSubscriptionActive === true;
  }

  get firstname() { return AuthModule.firstname; }
  get lastname()  { return AuthModule.lastname; }
  get logo()      { return AuthModule.logo; }
  get photoImage(){ return AuthModule.photo; }
  get backendUrl(){ return BACKEND_URL; }

  async logout() {
    await AuthModule.logout();
    this.$router.push({ name: 'login' });
  }

  isStepLocked(sIdx: number): boolean {
    const step = this.steps[sIdx];
    if (!step || step.chapters.length === 0) return false;
    return step.chapters.every(function(ch: Chapter) { return ch.isLocked === true; });
  }

  isStepUnavailable(sIdx: number): boolean {
    const step = this.steps[sIdx];
    if (!step || step.chapters.length === 0) return false;
    return step.chapters.every(function(ch: Chapter) { return ch.isAvailable === false; });
  }


  handleLockedStepClick(sIdx: number) {
  const step = this.steps[sIdx];
  if (!step) return;
  const isLocked = step.chapters.every(function(ch: Chapter) { return ch.isLocked === true; });
  const isUnavailable = step.chapters.every(function(ch: Chapter) { return ch.isAvailable === false; });
  if (isUnavailable) {
    this.lockedPopupType = 'unavailable';
    this.showLockedPopup = true;
  } else if (isLocked && !this.userHasSubscription) {
    this.lockedPopupType = 'locked';
    this.showLockedPopup = true;
  } else {
    this.selectStep(sIdx);
  }
}




handleLockedChapterClick(sIdx: number, cIdx: number) {
  const step = this.steps[sIdx];
  if (!step) return;
  const ch = step.chapters[cIdx];
  if (!ch) return;
  if (ch.isAvailable === false) {
    this.lockedPopupType = 'unavailable';
    this.showLockedPopup = true;
  } else if (ch.isLocked && !this.userHasSubscription) {
    this.lockedPopupType = 'locked';
    this.showLockedPopup = true;
  } else {
    if (!ch.videoUrl && this.isTopLevel(ch)) {
      const firstChildIdx = step.chapters.findIndex(
        (c, i) => i > cIdx && this.isChildOf(c, ch) && c.videoUrl && c.isAvailable !== false && !(c.isLocked && !this.userHasSubscription)
      );
      if (firstChildIdx !== -1) {
        this.selectChapter(sIdx, firstChildIdx);
        return;
      }
    }
    this.selectChapter(sIdx, cIdx);
  }
}


  goToSubscription() {
    this.$router.push({ name: 'subscription' });
  }

  async completeTrial(): Promise<void> {
      try {
        const { putUserTrialComplete } = await import('@/api/api');
        await putUserTrialComplete();
      } catch (e) {
        console.error('Could not mark trial complete', e);
      }
    }

    async goToPlatform(): Promise<void> {
      await this.completeTrial();
      sessionStorage.setItem('trial_completed', '1');
      if (!localStorage.getItem('service')) {
        localStorage.setItem('service', 'immo');
      }
      this.$router.push({ name: 'homePrescriber' });
    }
  
  goToChapter(sIdx: number, cIdx: number) {
    this.showTrialPopup = false;
    this.selectChapter(sIdx, cIdx);
  }

  toggleExpand(sIdx: number) {
    this.$set(this.expandedSteps, sIdx, !this.expandedSteps[sIdx]);
  }
  
  @Watch('currentStep') onStepChange() { this.$nextTick(this.safeLoadVideo); }
  @Watch('currentChapterIdx') onChChange() { this.$nextTick(this.safeLoadVideo); }
  async mounted() {
    document.addEventListener('mousemove', this.resetInactivityTimer);
    this.setPrimaryColor(this.getUserPrimaryColor());
    await AuthModule.initExistingUserSession();
  }

  setPrimaryColor(hexColor: string): void {
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    document.documentElement.style.setProperty('--primary-color', hexColor);
    document.documentElement.style.setProperty('--vs-primary', `${r}, ${g}, ${b}`);
    document.documentElement.style.setProperty('--vs-personal_color', `${r}, ${g}, ${b}`);
  }

  getUserPrimaryColor(): string {
    const authJson = localStorage.getItem('auth');
    if (authJson) {
      try {
        const auth = JSON.parse(authJson);
        return auth.personal_color || '#891A2B';
      } catch {
        return '#891A2B';
      }
    }
    return '#891A2B';
  }
  beforeDestroy() { 
    document.removeEventListener('mousemove', this.resetInactivityTimer); 
    clearTimeout(this.inactivityTimer);
  }

  // *************************** Tracking Progress ****************************************/
      async saveProgressToBackend(chapterId: number, watchedSeconds: number, lastPosition: number, isCompleted: boolean) {
            try {
              await postTrialProgress({
                chapterId,
                watchedSeconds,
                lastPosition,
                isCompleted
              });
            } catch (e) {
              console.error('Error saving progress:', e);
            }
          }

      async loadProgressFromBackend() {
          try {
            const response = await getTrialProgress();
            const progressData = response.data;
            
            progressData.forEach((p: any) => {
              this.steps.forEach((step, sIdx) => {
                step.chapters.forEach((ch, cIdx) => {
                  if (ch.id === p.chapterId) {
                    if (!this.allChapterWatchTime[sIdx]) {
                      this.$set(this.allChapterWatchTime, sIdx, []);
                      this.$set(this.allChapterCompleted, sIdx, []);
                    }
                    this.$set(this.allChapterWatchTime[sIdx], cIdx, p.watchedSeconds);
                    this.$set(this.allChapterCompleted[sIdx], cIdx, p.isCompleted);
                  }
                });
              });
            });
          } catch (e) {
            console.error('Error loading progress:', e);
          }
        }



        // Video Player Loading
        isVideoBuffering: boolean = false;
  videoVolume: number = 1;
  previousVolume: number = 1;

        safeLoadVideo() {
          this.isVideoBuffering = true; // Trigger loader immediately on source change
          const v = this.$refs.videoPlayer as HTMLVideoElement;
          if (v) { v.pause(); v.load(); }
        }

        rewind10() {
          const v = this.$refs.videoPlayer as HTMLVideoElement;
          if (!v) return;
          v.currentTime = Math.max(0, v.currentTime - 10);
        }

        toggleMute() {
          const v = this.$refs.videoPlayer as HTMLVideoElement;
          if (!v) return;
          if (this.videoVolume > 0) {
            this.previousVolume = this.videoVolume;
            this.videoVolume = 0;
          } else {
            this.videoVolume = this.previousVolume || 1;
          }
          v.volume = this.videoVolume;
        }

        onVolumeChange(e: Event) {
          const v = this.$refs.videoPlayer as HTMLVideoElement;
          if (!v) return;
          const val = parseFloat((e.target as HTMLInputElement).value);
          this.videoVolume = val;
          v.volume = val;
          if (val > 0) this.previousVolume = val;
        }

}
